import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { toastVariants, transition } from '../lib/motion.js';
import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

function formatEuros(cents) {
  const value = Math.max(0, Number(cents) || 0) / 100;
  return value.toLocaleString('fr-FR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Annonce broadcast quand le DJ accepte une suggestion.
 * Visible par tous les clients connectés à la soirée.
 */
export default function AcceptedSongBanner({
  accepted,
  onDismiss,
  paidQueueUiEnabled = true,
}) {
  const lastShownRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!accepted) return;
    if (lastShownRef.current === accepted.at) return;
    lastShownRef.current = accepted.at;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
    try {
      confetti({
        particleCount: 90,
        spread: 80,
        startVelocity: 38,
        ticks: 140,
        zIndex: 1900,
        origin: { y: 0.22 },
        colors: ['#ca8a04', '#a16207', '#f59e0b', '#22c55e', '#fafaf9'],
      });
    } catch {
      /* ignore */
    }
  }, [accepted]);

  const showAmount = paidQueueUiEnabled && accepted && accepted.amountCents > 0;

  return (
    <AnimatePresence>
      {accepted ? (
        <motion.div
          className="pointer-events-none fixed inset-x-0 top-[max(env(safe-area-inset-top),16px)] z-[75] flex justify-center px-3"
          variants={toastVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'medium')}
        >
          <button
            type="button"
            onClick={() => onDismiss?.()}
            className="pointer-events-auto flex w-full max-w-[460px] items-start gap-3 rounded-2xl border border-live/35 bg-bg-card px-4 py-3 text-left text-white"
            aria-label={`Le DJ a accepté ${accepted.song}${accepted.artist ? ` de ${accepted.artist}` : ''}. Toucher pour fermer.`}
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-2xl">
              🎉
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-live">
                Le DJ a accepté ce morceau !
              </p>
              <p className="mt-0.5 truncate text-base font-extrabold leading-tight">
                {accepted.song}
              </p>
              {accepted.artist ? (
                <p className="truncate text-xs text-white/80">{accepted.artist}</p>
              ) : null}
              <p className="mt-1 truncate text-[12px] text-white/85">
                {paidQueueUiEnabled ? 'offerte par ' : 'proposée par '}
                <span className="font-semibold text-white">{accepted.donor}</span>
                {showAmount ? (
                  <>
                    {' '}
                    <span className="text-white/55">·</span>{' '}
                    <span className="font-mono font-semibold tabular-nums text-gold">
                      +{formatEuros(accepted.amountCents)} €
                    </span>
                  </>
                ) : null}
              </p>
            </div>
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
